import React from "react";
import { getSeriesColor } from "features/analytics/studio/utils/chartColors";
import { formatNumber } from "shared/utils/numberFormatter";
import { PRIMARY_HEX } from "shared/theme/trodoColors";

// KPI metric card colors (gray scale)
const LABEL_COLOR = "var(--text-muted)";   // gray-500
const VALUE_COLOR = "var(--text-primary)";   // gray-900
const UNIT_COLOR = "var(--text-disabled)";    // gray-400
const CHART_COLORS = [
  PRIMARY_HEX,
  "#8b5cf6",
  "#10b981",
  "var(--feedback-warning)",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
];

/**
 * Format numeric value for metric display: K, M, B, T or comma-separated.
 */
export function formatMetricValue(value, options = {}) {
  const { decimals = 1, useAbbreviation = true } = options;
  const num = Number(value);
  if (isNaN(num)) return "0";
  return useAbbreviation ? formatNumber(num, decimals) : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function getUnitLabel(seriesItem) {
  const name = (seriesItem?.metricName || "").toLowerCase();
  if (name.includes("unique") || name.includes("uniques")) return "users";
  if (name.includes("session")) return "sessions";
  return "events";
}

/** Value font size by cards-per-row: 1 > 2 > 3 > 4 (largest to smallest) */
const VALUE_SIZE_BY_CARDS = { 1: 72, 2: 64, 3: 52, 4: 44 };
const LABEL_SIZE_BY_CARDS = { 1: 15, 2: 14, 3: 13, 4: 12 };
const UNIT_SIZE_BY_CARDS = { 1: 14, 2: 13, 3: 12, 4: 11 };

/**
 * Single metric block (no box, no border, no shadow).
 * Font sizes scale by cardsInRow: 1 card (largest) → 4 cards (smallest).
 * When hasBreakdown: tooltip shows full metric name, breakdown value, and value + unit.
 */
function MetricBlock({
  identifier,
  title,
  value,
  unit,
  color,
  isSingle,
  centerContent,
  tooltip,
  cardsInRow = 1,
  /** When set (e.g. funnel % or duration), shown instead of formatting `value` */
  valueDisplay: valueDisplayOverride,
  /** When true, omit the colored square + label row (parent already shows metric + total). */
  hideTitleRow = false,
  contentScale = 1,
}) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });
  const displayValue =
    valueDisplayOverride != null && valueDisplayOverride !== ""
      ? String(valueDisplayOverride)
      : formatMetricValue(value);
  const tier = Math.min(Math.max(cardsInRow, 1), 4);
  const sc = typeof contentScale === "number" && Number.isFinite(contentScale) ? contentScale : 1;
  const valueSizePx = Math.max(22, Math.round(VALUE_SIZE_BY_CARDS[tier] * sc));
  const labelSizePx = Math.max(10, Math.round(LABEL_SIZE_BY_CARDS[tier] * sc));
  const unitSizePx = Math.max(9, Math.round(UNIT_SIZE_BY_CARDS[tier] * sc));
  const textAlign = centerContent ? "center" : "left";
  const justifyContent = centerContent ? "center" : "flex-start";

  const handleMouseEnter = (e) => {
    if (tooltip) {
      setShowTooltip(true);
      setTooltipPosition({
        x: e.clientX,
        y: e.clientY
      });
    }
  };

  const handleMouseMove = (e) => {
    if (tooltip && showTooltip) {
      setTooltipPosition({
        x: e.clientX,
        y: e.clientY
      });
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div
      className="flex flex-col h-full relative group"
      style={{
        fontFamily: "Inter, sans-serif",
        textAlign,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Top row: colored square + label */}
      {!hideTitleRow && (
        <div
          className="flex items-center gap-2"
          style={{
            marginBottom: Math.max(8, Math.round(16 * sc)),
            justifyContent,
          }}
        >
          <div
            className="w-3 h-3 rounded flex-shrink-0"
            style={{ backgroundColor: color }}
            aria-hidden
          />
          <span
            className="truncate"
            style={{
              fontSize: labelSizePx,
              fontWeight: 500,
              color: LABEL_COLOR,
            }}
            title={tooltip ? `${identifier}. ${tooltip.fullMetricName}\n${tooltip.breakdownValue}\n${displayValue} ${unit}` : undefined}
          >
            {identifier}. {title}
          </span>
        </div>
      )}
      {/* Main value: size scales by cards per row */}
      <div
        className="transition-opacity duration-200"
        style={{
          fontSize: valueSizePx,
          fontWeight: 800,
          color: VALUE_COLOR,
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
        }}
      >
        {displayValue}
      </div>
      {/* Unit: scales with tier */}
      <div
        style={{
          marginTop: Math.max(4, Math.round(8 * sc)),
          fontSize: unitSizePx,
          fontWeight: 400,
          color: UNIT_COLOR,
        }}
      >
        {unit}
      </div>
      {/* Hover tooltip (breakdown cards): full metric name, breakdown value, value + unit: follows cursor */}
      {tooltip && showTooltip && (
        <div
          className="fixed z-50 p-4 rounded-lg shadow-lg border min-w-[200px] pointer-events-none"
          style={{
            left: `${tooltipPosition.x + 10}px`, // Offset to the right of cursor
            top: `${tooltipPosition.y - 10}px`,  // Offset above cursor
            backgroundColor: "var(--surface-base)",
            borderColor: "var(--border-default)",
            borderLeftWidth: 4,
            borderLeftColor: color,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600, color: VALUE_COLOR, marginBottom: 4 }}>
            {identifier}. {tooltip.fullMetricName}
          </div>
          <div style={{ fontSize: 13, color: LABEL_COLOR, marginBottom: 4 }}>
            {tooltip.breakdownValue}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: VALUE_COLOR }}>
            {displayValue} {unit}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Layout class names by metric count (for automatic layout).
 * 1 → centered-single-metric
 * 2 → two-column-layout (with divider on desktop)
 * 3+ → responsive-grid-layout
 */
export function getMetricLayoutClass(count) {
  if (count <= 0) return "";
  if (count === 1) return "centered-single-metric";
  if (count === 2) return "two-column-layout";
  return "responsive-grid-layout";
}

/**
 * Outer wrapper: 60px top spacing, flat white, no card styling.
 */
export default function MetricCards({
  series = [],
  seriesColors,
  /** Single KPI: hide label row when the page/card header already shows metric name + total. */
  hideTitleRow = false,
  /** Board row resize: scale value/label typography */
  contentScale = 1,
}) {
  if (!Array.isArray(series) || series.length === 0) return null;

  const sc = typeof contentScale === "number" && Number.isFinite(contentScale) ? contentScale : 1;
  const count = series.length;
  const isSingle = count === 1;
  const isTwo = count === 2;
  const layoutClass = getMetricLayoutClass(count);
  const topPad = Math.max(12, Math.round((hideTitleRow && isSingle ? 24 : 60) * sc));

  return (
    <div
      className={layoutClass}
      style={{
        paddingTop: topPad,
        paddingBottom: Math.max(16, Math.round(48 * sc)),
        width: "100%",
        backgroundColor: "var(--surface-base)",
      }}
    >
      {/* 1 metric: single centered column, full width */}
      {isSingle && (
        <div className="w-full max-w-2xl mx-auto">
          {series.map((s, index) => {
            const value = s.summary?.total ?? (s.data || []).reduce((acc, p) => acc + (p.value || 0), 0);
            const identifier = s.metricIdentifier ?? String.fromCharCode(65 + index);
            const title = s.displayLabel ?? s.metricName ?? `Metric ${identifier}`;
            const unit = s.unit ?? getUnitLabel(s);
            const color = getSeriesColor(index, seriesColors || CHART_COLORS);
            const tooltip = s.fullMetricName ? { fullMetricName: s.fullMetricName, breakdownValue: s.displayLabel ?? s.originalBreakdownKey ?? s.metricName } : null;
            return (
              <MetricBlock
                key={`${s.metricId ?? "m"}-${index}`}
                identifier={identifier}
                title={title}
                value={value}
                valueDisplay={s.valueDisplay}
                unit={unit}
                color={color}
                isSingle={true}
                centerContent={true}
                tooltip={tooltip}
                cardsInRow={1}
                hideTitleRow={hideTitleRow}
                contentScale={contentScale}
              />
            );
          })}
        </div>
      )}

      {/* 2 metrics: two equal columns, 40px between blocks */}
      {isTwo && (
        <div className="flex flex-col sm:flex-row w-full items-stretch" style={{ gap: 40 }}>
          {series.map((s, index) => {
            const value = s.summary?.total ?? (s.data || []).reduce((acc, p) => acc + (p.value || 0), 0);
            const identifier = s.metricIdentifier ?? String.fromCharCode(65 + index);
            const title = s.displayLabel ?? s.metricName ?? `Metric ${identifier}`;
            const unit = s.unit ?? getUnitLabel(s);
            const color = getSeriesColor(index, seriesColors || CHART_COLORS);
            const tooltip = s.fullMetricName ? { fullMetricName: s.fullMetricName, breakdownValue: s.displayLabel ?? s.originalBreakdownKey ?? s.metricName } : null;
            return (
              <div
                key={`${s.metricId ?? "m"}-${index}`}
                className={`flex-1 min-w-0 ${index === 0 ? "mb-10 sm:mb-0" : ""}`}
                style={{ minHeight: 140 }}
              >
                <MetricBlock
                  identifier={identifier}
                  title={title}
                  value={value}
                  valueDisplay={s.valueDisplay}
                  unit={unit}
                  color={color}
                  isSingle={false}
                  centerContent={true}
                  tooltip={tooltip}
                  cardsInRow={2}
                  hideTitleRow={hideTitleRow}
                  contentScale={contentScale}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* 3+ metrics: responsive grid, 1→2→3→4 cols, 40px gap */}
      {!isSingle && !isTwo && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full"
          style={{ gap: 40 }}
        >
          {series.map((s, index) => {
            const value = s.summary?.total ?? (s.data || []).reduce((acc, p) => acc + (p.value || 0), 0);
            const identifier = s.metricIdentifier ?? String.fromCharCode(65 + index);
            const title = s.displayLabel ?? s.metricName ?? `Metric ${identifier}`;
            const unit = s.unit ?? getUnitLabel(s);
            const color = getSeriesColor(index, seriesColors || CHART_COLORS);
            const tooltip = s.fullMetricName ? { fullMetricName: s.fullMetricName, breakdownValue: s.displayLabel ?? s.originalBreakdownKey ?? s.metricName } : null;
            const cardsInRow = Math.min(count, 4);
            return (
              <div key={`${s.metricId ?? "m"}-${index}`} style={{ minHeight: 140 }}>
                <MetricBlock
                  identifier={identifier}
                  title={title}
                  value={value}
                  valueDisplay={s.valueDisplay}
                  unit={unit}
                  color={color}
                  isSingle={false}
                  centerContent={true}
                  tooltip={tooltip}
                  cardsInRow={cardsInRow}
                  hideTitleRow={hideTitleRow}
                  contentScale={contentScale}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
